import type { Locale, RoleContract, SchoolSettingsInputContract } from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { Button } from "@chams-pay/design-system/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@chams-pay/design-system/components/dialog";
import { Input } from "@chams-pay/design-system/components/input";
import { Label } from "@chams-pay/design-system/components/label";
import { ArrowRight, CircleCheck, LoaderCircle, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSchoolSettings } from "../core/use-operations.js";
import { Bidi, Count } from "./figures.js";
import { ScopedError, SkeletonLines } from "./states.js";

const feeFields = [
  ["preschoolRegistration", "settings.preschoolRegistration"],
  ["primaryRegistration", "settings.primaryRegistration"],
  ["preschoolTuition", "settings.preschoolTuition"],
  ["primaryTuition", "settings.primaryTuition"],
  ["transport", "settings.transportFee"],
  ["accompaniment", "settings.accompanimentFee"],
] as const;

function SettingsSection({
  title,
  help,
  children,
}: {
  readonly title: string;
  readonly help?: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 shadow-e1">
      <div className="flex flex-col gap-1">
        <h3 className="font-heading text-[15px] font-bold">{title}</h3>
        {help && <p className="text-[11.5px] leading-relaxed text-muted-foreground">{help}</p>}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  value,
  dir,
  type = "text",
  inputClassName,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly dir?: "ltr" | "rtl";
  readonly type?: "text" | "email" | "tel" | "number";
  readonly inputClassName?: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <Label className="flex flex-col items-stretch gap-1.5 text-start">
      <span className="font-heading text-[11px] font-bold text-foreground/70">{label}</span>
      <Input
        className={`h-11 ${inputClassName ?? ""}`}
        type={type}
        {...(type === "number" ? { min: 0, step: 1, inputMode: "numeric" as const } : {})}
        {...(dir ? { dir } : {})}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Label>
  );
}

export function SchoolSettingsPanel({
  locale,
  csrf,
  role,
  mobile = false,
  refreshRevision,
  onSaved,
  onAdvanceYearActivated,
}: {
  readonly locale: Locale;
  readonly csrf: string;
  readonly role: RoleContract;
  readonly mobile?: boolean;
  readonly refreshRevision?: number;
  readonly onSaved?: () => void;
  readonly onAdvanceYearActivated?: () => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const controller = useSchoolSettings(csrf, {
    ...(onSaved ? { onSaved } : {}),
  });
  const [advanceConfirmed, setAdvanceConfirmed] = useState(false);
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [advanceFeedbackVisible, setAdvanceFeedbackVisible] = useState(false);
  const lastRefreshRevision = useRef(refreshRevision);
  const { draft, advanceYearDraft } = controller;
  useEffect(() => {
    if (!advanceFeedbackVisible) return;
    const timer = window.setTimeout(() => setAdvanceFeedbackVisible(false), 2_250);
    return () => window.clearTimeout(timer);
  }, [advanceFeedbackVisible]);
  useEffect(() => {
    if (refreshRevision === undefined || refreshRevision === lastRefreshRevision.current) return;
    lastRefreshRevision.current = refreshRevision;
    controller.reload();
  }, [controller.reload, refreshRevision]);

  if (controller.loading)
    return (
      <div className="rounded-lg border border-border bg-card p-5">
        <SkeletonLines />
      </div>
    );
  if ((!draft || !controller.settings) && controller.failed)
    return (
      <ScopedError locale={locale} title={t("settings.loadFailed")} onRetry={controller.reload} />
    );
  if (!draft || !controller.settings) return null;

  const patch = (value: Partial<SchoolSettingsInputContract>) =>
    controller.setDraft({ ...draft, ...value });
  const patchAmount = (key: keyof SchoolSettingsInputContract["amountsMad"], value: string) =>
    patch({ amountsMad: { ...draft.amountsMad, [key]: value } });
  const saveDisabled =
    controller.saving ||
    (role === "system-administrator" &&
      (!draft.schoolNameAr.trim() || !draft.schoolNameFr.trim() || !draft.classes.length)) ||
    !Object.values(draft.amountsMad).every((amount) => /^\d+$/.test(amount));
  const amountInputClassName = locale === "ar" ? "text-right" : "text-left";
  const hasPrimaryCycle = draft.classes.some((entry) => entry.cycle === "primary");
  const mappedPrimaryTuitionLevels = draft.classes
    .filter((entry) => entry.cycle !== "primary" && entry.tuitionTariff === "primary")
    .map((entry) => entry.label);
  const visibleFeeFields = feeFields.filter(([key]) => {
    if (key === "primaryRegistration") return hasPrimaryCycle;
    if (key === "primaryTuition") return hasPrimaryCycle || mappedPrimaryTuitionLevels.length > 0;
    return true;
  });
  const confirmAdvanceYearActivation = async () => {
    if (!(await controller.activateAdvanceYear())) return;
    setAdvanceConfirmed(false);
    setAdvanceDialogOpen(false);
    setAdvanceFeedbackVisible(true);
    onAdvanceYearActivated?.();
  };

  return (
    <div className="school-settings-panel flex flex-col gap-4">
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void controller.save();
        }}
      >
        {role === "system-administrator" && (
          <SettingsSection title={t("settings.schoolIdentity")}>
            <div className="grid gap-3 md:grid-cols-2">
              <Field
                label={t("settings.schoolNameAr")}
                value={draft.schoolNameAr}
                dir="rtl"
                onChange={(schoolNameAr) => patch({ schoolNameAr })}
              />
              <Field
                label={t("settings.schoolNameFr")}
                value={draft.schoolNameFr}
                dir="ltr"
                onChange={(schoolNameFr) => patch({ schoolNameFr })}
              />
              <Field
                label={t("settings.schoolEmail")}
                value={draft.email}
                type="email"
                dir="ltr"
                onChange={(email) => patch({ email })}
              />
              <Field
                label={t("settings.schoolPhone")}
                value={draft.phone}
                type="tel"
                dir="ltr"
                onChange={(phone) => patch({ phone })}
              />
              <Field
                label={t("settings.addressAr")}
                value={draft.addressAr}
                dir="rtl"
                onChange={(addressAr) => patch({ addressAr })}
              />
              <Field
                label={t("settings.addressFr")}
                value={draft.addressFr}
                dir="ltr"
                onChange={(addressFr) => patch({ addressFr })}
              />
            </div>
          </SettingsSection>
        )}

        <SettingsSection
          title={t("settings.financialSettings")}
          help={t("settings.prospectiveFees")}
        >
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {visibleFeeFields.map(([key, label]) => (
              <Field
                key={key}
                label={
                  key === "primaryTuition" && !hasPrimaryCycle
                    ? `${locale === "ar" ? "واجب الدراسة" : "Scolarité"} — ${mappedPrimaryTuitionLevels.join(" / ")}`
                    : t(label)
                }
                value={draft.amountsMad[key]}
                type="number"
                dir="ltr"
                inputClassName={amountInputClassName}
                onChange={(value) => patchAmount(key, value)}
              />
            ))}
          </div>
          {mobile && role === "financial-administrator" && (
            <Button type="submit" className="mt-1 min-h-11 w-full" disabled={saveDisabled}>
              {t("settings.saveFees")}
            </Button>
          )}
        </SettingsSection>

        {role === "system-administrator" && (
          <SettingsSection title={t("settings.classes")}>
            <div className="flex flex-col gap-2.5">
              {draft.classes.map((entry) => (
                <div
                  key={entry.label}
                  className="grid gap-2.5 rounded-md border border-border/70 bg-surface-2 p-3 md:grid-cols-3"
                >
                  <span className="flex flex-col gap-1.5 text-start">
                    <span className="font-heading text-[11px] font-bold text-foreground/70">
                      {t("settings.classLabel")}
                    </span>
                    <Bidi className="font-heading text-sm font-bold">{entry.label}</Bidi>
                  </span>
                  <span className="flex flex-col gap-1.5 text-start">
                    <span className="font-heading text-[11px] font-bold text-foreground/70">
                      {t("settings.cycle")}
                    </span>
                    <span className="font-heading text-sm">
                      {t(entry.cycle === "preschool" ? "settings.preschool" : "settings.primary")}
                    </span>
                  </span>
                  <span className="flex flex-col gap-1.5 text-start">
                    <span className="font-heading text-[11px] font-bold text-foreground/70">
                      {t("settings.nextClass")}
                    </span>
                    <Bidi className="font-heading text-sm font-bold">{entry.nextClassLabel}</Bidi>
                  </span>
                </div>
              ))}
            </div>
          </SettingsSection>
        )}

        {controller.saved && (
          <p
            role="status"
            className="flex items-center gap-2 rounded-md border border-success-border bg-success-soft px-4 py-3 text-xs text-success"
          >
            <CircleCheck aria-hidden="true" className="size-4" />
            {t("settings.saved")}
          </p>
        )}
        {controller.failed && (
          <p
            role="alert"
            className="rounded-md border border-destructive-border bg-destructive-soft px-4 py-3 text-xs text-destructive"
          >
            {t("ops.genericErrorBody")}
          </p>
        )}
        {(!mobile || role === "system-administrator") && (
          <Button type="submit" className="w-fit min-h-11" disabled={saveDisabled}>
            {t(role === "financial-administrator" ? "settings.saveFees" : "settings.saveGlobal")}
          </Button>
        )}
      </form>

      {(advanceYearDraft || controller.settings.advanceYear) && (
        <SettingsSection
          title={t("settings.advanceYear")}
          help={t(
            controller.settings.advanceYear
              ? "settings.advanceYearActiveHelp"
              : "settings.advanceYearHelp",
          )}
        >
          <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-3">
            <div className="flex flex-col gap-1.5 rounded-md border border-border bg-surface-2 px-4 py-3">
              <span className="chams-eyebrow">{t("settings.currentSchoolYear")}</span>
              <Bidi className="font-heading text-sm font-bold">
                {controller.settings.academicYear}
              </Bidi>
            </div>
            <ArrowRight
              aria-hidden="true"
              className="size-5 self-center text-muted-2 rtl:-scale-x-100"
            />
            <div className="flex flex-col gap-1.5 rounded-md border border-primary/35 bg-primary-soft px-4 py-3">
              <span className="chams-eyebrow text-primary-strong/70">
                {t("settings.nextSchoolYear")}
              </span>
              <Bidi className="font-heading text-sm font-bold text-primary-strong">
                {advanceYearDraft?.academicYear ??
                  controller.settings.advanceYear?.academicYear ??
                  ""}
              </Bidi>
            </div>
          </div>
          {advanceYearDraft ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {feeFields.map(([key, label]) => (
                  <Field
                    key={key}
                    label={t(label)}
                    value={advanceYearDraft.amountsMad[key]}
                    type="number"
                    dir="ltr"
                    inputClassName={amountInputClassName}
                    onChange={(value) =>
                      controller.setAdvanceYearDraft({
                        ...advanceYearDraft,
                        amountsMad: { ...advanceYearDraft.amountsMad, [key]: value },
                      })
                    }
                  />
                ))}
              </div>
              <p className="flex items-start gap-2 rounded-md border border-warning-border bg-warning-soft px-4 py-3 text-[11.5px] leading-relaxed text-warning">
                <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                {t("settings.advanceYearWarning")}
              </p>
              <Label className="flex min-h-11 items-center gap-2.5 text-xs font-semibold">
                <input
                  type="checkbox"
                  className="size-5 accent-[rgb(var(--primary))]"
                  checked={advanceConfirmed}
                  onChange={(event) => setAdvanceConfirmed(event.target.checked)}
                />
                {t("settings.advanceYearConfirm")}
              </Label>
              <Button
                type="button"
                className={mobile ? "min-h-11 w-full" : "min-h-11 w-fit"}
                disabled={
                  !advanceConfirmed ||
                  controller.activatingAdvanceYear ||
                  !Object.values(advanceYearDraft.amountsMad).every((amount) =>
                    /^\d+$/.test(amount),
                  )
                }
                onClick={() => setAdvanceDialogOpen(true)}
              >
                {t("settings.advanceYearAction")}
              </Button>
            </>
          ) : (
            <p
              role="status"
              className="flex flex-wrap items-center gap-2 rounded-md border border-success-border bg-success-soft px-4 py-3 text-xs text-success"
            >
              <CircleCheck aria-hidden="true" className="size-4" />
              {t("settings.advanceYearActive")}{" "}
              <strong>
                <Count value={controller.settings.advanceYear?.eligibleStudents ?? 0} />{" "}
                {t("settings.studentsEligible")} ·{" "}
                <Count value={controller.settings.advanceYear?.obligationsCreated ?? 0} />{" "}
                {t("settings.obligationsCreated")}
              </strong>
            </p>
          )}
        </SettingsSection>
      )}

      <Dialog
        open={advanceDialogOpen}
        onOpenChange={(open) => {
          if (!controller.activatingAdvanceYear) setAdvanceDialogOpen(open);
        }}
      >
        <DialogContent showCloseButton={false} className="gap-5 p-5 sm:max-w-lg">
          <DialogHeader className="gap-2">
            <DialogTitle>{t("settings.advanceYearDialogTitle")}</DialogTitle>
            <DialogDescription className="leading-relaxed">
              {t("settings.advanceYearDialogBody")}
            </DialogDescription>
          </DialogHeader>
          {advanceYearDraft && (
            <div className="flex flex-col gap-3 rounded-md border border-border bg-surface-2 p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="chams-eyebrow">{t("settings.nextSchoolYear")}</span>
                <Bidi className="font-heading text-sm font-bold">
                  {advanceYearDraft.academicYear}
                </Bidi>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {feeFields.map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-muted-foreground">{t(label)}</span>
                    <Bidi className="font-heading font-bold">
                      {advanceYearDraft.amountsMad[key]} MAD
                    </Bidi>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-3 border-t border-border pt-3 text-xs">
                <span>{t("settings.studentsEligible")}</span>
                <Count value={controller.settings.advanceYearEligibleStudents} />
              </div>
            </div>
          )}
          {controller.advanceYearFailed && (
            <p
              role="alert"
              className="rounded-md border border-destructive-border bg-destructive-soft px-4 py-3 text-xs text-destructive"
            >
              {t("settings.advanceYearActionFailed")}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={controller.activatingAdvanceYear}
              onClick={() => setAdvanceDialogOpen(false)}
            >
              {t("settings.rolloverDialogCancel")}
            </Button>
            <Button
              type="button"
              disabled={controller.activatingAdvanceYear}
              aria-busy={controller.activatingAdvanceYear}
              onClick={() => void confirmAdvanceYearActivation()}
            >
              {controller.activatingAdvanceYear && (
                <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
              )}
              {t(
                controller.activatingAdvanceYear
                  ? "settings.advanceYearActivating"
                  : "settings.advanceYearDialogConfirm",
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {advanceFeedbackVisible && (
        <div
          role="status"
          aria-live="polite"
          className={`pointer-events-none fixed start-1/2 z-[80] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-full border border-white/15 bg-foreground/[0.92] px-4 py-2.5 text-center font-heading text-xs font-bold text-white shadow-e3 rtl:translate-x-1/2 ${
            mobile ? "bottom-[calc(var(--mobile-bottom-nav-offset)+1rem)]" : "bottom-6"
          }`}
        >
          {t("settings.advanceYearActive")}
        </div>
      )}
    </div>
  );
}
